import Axios from "axios";
import moment from "moment-timezone";
import React, { useEffect, useState } from "react";
import { Form, Button, Row, Col } from "react-bootstrap";
import { useForm, useFieldArray } from "react-hook-form";
import { dirname } from "../../../../../util/resourcePath";
import Noti from "../../../../../util/noti";
import { useRouter } from "../../../../../util/router";
import { useUser } from "../../../../../util/user";
import UserFormElementsKC from "./UserFormElementsKC";
import "./UsersFormKC.css";
import GYAxios from "../../../../../util/GYAxios";
import { useKeycloakUsersFormContext } from "../../../../../util/keycloakUsersFormContext";

const FormBodyKC = (props) => {
	const directory = dirname();
	const loggedInUser = useUser();
	const router = useRouter();
	const { selectedKeycloakUsers, setSelectedKeycloakUsers } = useKeycloakUsersFormContext();

	const [deptSelectOptions, setDeptSelectOptions] = useState([]);
	const [countriesOptions, setCountriesOptions] = useState([]);

	const existingUsers = props.existingUsers;

	const formBtnStyle = {
		width: "15%",
		minWidth: "120px",
		margin: "auto",
		display: "block",
	};

	const defaultUserObj = {
		user_id: '',
		dept_id: "",
		email: "",
		first_name: "",
		last_name: "",
		nick_name: "",
		position: "",
		seesInvoices: false,
		seesReports: false,
		isExternal: false,
	};

	const usersObj = selectedKeycloakUsers?.map(user => ({ ...user, seesInvoices: !!user.see_invoice, seesReports: !!user.see_report, isExternal: !!user.is_external })) || [defaultUserObj];
	const { register, control, handleSubmit, reset, setValue, errors } = useForm({ defaultValues: { usersObj } });

	const { fields, append, remove } = useFieldArray({
		control,
		name: "usersObj",
	});

	window.remove = remove;
	window.reset = reset;

	/**
	 * Handle form submission
	 * @param {obj} submittedData
	 */
	const onSubmit = (submittedData) => {
		// Add User Confirmation
		const confirmSubmit = window.confirm(`Confirm Submit Data?`);

		// Confirm submit users
		if (!confirmSubmit) { return }

		const usersObj = submittedData.usersObj;
		const formattedUsers = usersObj.map(prepareUser);

		// Get Insert API Requests
		const usersToInsert = formattedUsers.filter(user => user.id === '');
		const insertUserRequests = usersToInsert.map(getInsertReq);

		// Get Update API Requests
		const usersToUpdate = formattedUsers.filter(user => user.id !== '');
		const updateUserRequests = usersToUpdate.map(getUpdateReq);

		// Making API Calls
		Promise.all([...insertUserRequests, ...updateUserRequests]).then(responses => {
			const updateErrors = responses.filter((res) => res.data.affectedRows !== 1 && res.data.serverStatus !== 2);

			if (updateErrors.length > 0) {
				return Noti.create({
					type: "e",
					msg: "Add new users Error!",
				});
			}

			Noti.create({
				type: "s",
				msg: "Users Created Successfully",
			});

			// Return to client user table
			setTimeout(() => router.replace(`/solar/administration/users/GY`), 1300);
		});

		setSelectedKeycloakUsers(null);
	};

	/**
	 * Prepare user obj for form submission
	 * @param {obj} user
	 * @returns Formatter user obj
	 */
	const prepareUser = (_user) => {
		const user = { ..._user };
		const userID = loggedInUser.preferred_username;
		const now = moment().format("YYYY-MM-DD HH:mm:ss");

		const id = user.user_id;
		const see_invoice = user.seesInvoices;
		const see_report = user.seesReports;
		const is_external = user.isExternal;
		const client_id = "all";
		const site_id = "all";

		// Additional data
		// const emailSplit = user.email.split("@");
		// const username = emailSplit[0];
		const username = user.email;

		delete user.seesInvoices;
		delete user.seesReports;
		delete user.isExternal;
		delete user.user_id;

		let formattedUser = {
			...user,
			id,
			see_invoice,
			see_report,
			is_external,
			last_updated_by: userID,
			last_updated: now,
		};

		if (id !== '') {
			formattedUser = { ...formattedUser, exported_status: 0 }
		}

		if (id === '') {
			formattedUser = {
				...formattedUser,
				type: 1,
				username,
				client_id,
				site_id,
				created_by: userID,
				created_at: now,
			};
		}

		// transform user payload
		for (let key in formattedUser) {
			// trim string
			if (typeof formattedUser[key] === 'string') {
				formattedUser[key] = formattedUser[key].trim();
			}
			// make boolean to binary number
			if (typeof formattedUser[key] === 'boolean') {
				formattedUser[key] = formattedUser[key] ? 1 : 0;
			}
		}
		return formattedUser;
	};

	/**
	 * Get Insert users promises
	 * @param {*} user
	 * @returns Insert user Promise
	 */
	const getInsertReq = _user => {
		const user = { ..._user };
		delete user.id;
		// Insert URI
		const usersURI = "/users/new";
		return GYAxios.post(usersURI, user);
	};

	const getUpdateReq = _user => {
		const user = { ..._user };
		const id = user.id;
		delete user.id;
		// Update URI
		const usersURI = `/users/${id}`;
		return GYAxios.patch(usersURI, user);
	}

	/**
	 * Fetch select options
	 */
	const fetchSelectOptions = () => {
		const deptSelectReq = GYAxios.get(`/select/user/departments`);
		const countriesSelectReq = GYAxios.get(`/select/countries`);

		const reqArray = [deptSelectReq, countriesSelectReq];

		Axios.all(reqArray).then(
			Axios.spread((...responses) => {
				const deptOpts =
					responses[0].data.error == null ? responses[0].data : [];
				const countriesRes =
					responses[1].data.error == null ? responses[1].data : [];
				const countriesOpts = countriesRes.filter((opt) => opt.value !== "ALL");

				setDeptSelectOptions(deptOpts);
				setCountriesOptions(countriesOpts);
			})
		);
	};
	/**
	 * Append new user to the form
	 */
	const appendNewUser = () => append(defaultUserObj);

	useEffect(() => {
		fetchSelectOptions();
	}, []);

	return (
		<div>
			<Form onSubmit={handleSubmit(onSubmit)} id="keycloak-users-form">
				{fields.map((item, index) => (
					<UserFormElementsKC
						item={item}
						index={index}
						errors={errors}
						register={register}
						control={control}
						setValue={setValue}
						remove={remove}
						existingUsers={existingUsers}
						deptSelectOptions={deptSelectOptions}
						countriesOptions={countriesOptions}
					/>
				))}

				<img
					src={`${directory}/images/add-form2.png`}
					alt="add user"
					style={{
						width: "5%",
						cursor: "pointer",
						opacity: "0.5",
						margin: "auto",
						display: "block",
					}}
					title="Add User"
					onClick={appendNewUser}
				/>
				<Row>
					<Col md="6">
						<Button
							variant="Secondary"
							style={formBtnStyle}
							onClick={() => { remove(); setTimeout(reset); }}
						>
							Reset
						</Button>
					</Col>
					<Col md="6">
						<Button
							variant="primary"
							style={formBtnStyle}
							type="submit"
							disabled={fields.length === 0}
						>
							Submit
						</Button>
					</Col>
				</Row>
			</Form>
			<Noti />
		</div>
	);
};

export default FormBodyKC;
